import React, { useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Authenticated } from '../../lib/auth/auth-components';
import { Button } from '../ui/button';
import { useToast } from '../ui/use-toast';
import { useAuth } from '../../lib/auth/AuthContext';

const AuthTestContent = () => {
  const { toast } = useToast();
  const testAuthResult = useQuery('languages:testAuthQuery' as any);
  const createLanguage = useMutation('languages:upsert' as any);
  const { isAuthenticated: authContextAuth, user, isLoading } = useAuth();
  
  useEffect(() => {
    // Log auth state on component mount
    console.log("Auth state in AuthTest:", {
      authContext: { isAuthenticated: authContextAuth, user },
      isLoading
    });
    
    // Check localStorage for session token
    const token = localStorage.getItem('sessionToken');
    console.log("Session token in localStorage:", token ? `${token.substring(0, 8)}...` : 'not found');
  }, [authContextAuth, user, isLoading]);
  
  const handleTestAdd = async () => {
    try {
      // Diagnostic log before making the request
      console.log("About to call createLanguage mutation");
      console.log("Auth state:", { isAuthenticated: authContextAuth, isLoading });
      
      const result = await createLanguage({
        code: 'en',
        name: 'English',
        isDefault: true,
        isActive: true,
        direction: 'ltr'
      });
      
      toast({
        title: 'Language added',
        description: 'Test language was added successfully',
      });
      
      console.log("Language add result:", result);
    } catch (error) {
      toast({
        title: 'Error adding language',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
      
      console.error("Language add error:", error);
    }
  };
  
  const handleSetManualToken = () => {
    // This is a manual test to explicitly set the token
    const token = localStorage.getItem('sessionToken');
    if (token && window.convex) {
      console.log("Manually setting token directly");
      window.convex.setAuth(async () => token);
      toast({
        title: 'Token set manually',
        description: 'Directly set the token through window.convex.setAuth'
      });
    } else {
      toast({
        title: 'Error',
        description: 'No token found in localStorage',
        variant: 'destructive'
      });
    }
  };
  
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Authentication Test</h1>
      
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Auth State</h2>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-gray-50 p-4 rounded">
            <h3 className="font-medium mb-2">AuthContext</h3>
            <div className="text-sm">
              <p>isAuthenticated: {authContextAuth ? 'true' : 'false'}</p>
              <p>User: {user ? user.email : 'none'}</p>
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded">
            <h3 className="font-medium mb-2">Auth State</h3>
            <div className="text-sm">
              <p>isAuthenticated: {authContextAuth ? 'true' : 'false'}</p>
              <p>isLoading: {isLoading ? 'true' : 'false'}</p>
            </div>
          </div>
        </div>
        
        <h2 className="text-xl font-semibold mb-4">Auth Test Result</h2>
        <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-60">
          {JSON.stringify(testAuthResult, null, 2)}
        </pre>
      </div>
      
      <div className="space-y-4">
        <div className="flex space-x-4">
          <Button onClick={handleTestAdd}>
            Test Add Language
          </Button>
          <Button variant="outline" onClick={handleSetManualToken}>
            Set Token Manually
          </Button>
        </div>
        <div className="text-sm text-gray-500 mt-4">
          <p>Note: After making changes to auth.config.js, be sure to run 'npx convex deploy'</p>
          <p>to update your Convex backend configuration.</p>
        </div>
      </div>
    </div>
  );
};

export const AuthTest = () => {
  return (
    <Authenticated>
      <AuthTestContent />
    </Authenticated>
  );
}; 